<script setup lang="ts">
import type { Housing } from '~/models/Housing';
import type { Offer } from '~/models/Offers';
import type { User } from '~/models/User';

const emit = defineEmits(['update:watch-list', 'update:more-actions-type']);

const props = defineProps({
  housing: {
    type: Object as PropType<Housing>,
    required: true,
  },
  offer: {
    type: Object as PropType<Offer>,
    required: false,
  },
  user: {
    type: Object as PropType<User>,
    required: true,
  },
  isLoggedIn: {
    type: Boolean,
    default: false,
  },
  isEditing: {
    type: Boolean,
    default: false,
  },
  isUsersOwnHousing: {
    type: Boolean,
    default: false,
  },
  watchListUpdateInProgress: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n({
  useScope: 'local',
});

const { getTrackingConfig } = useTrackingConfig();
const trackingButton = getTrackingConfig('ad', 'buttons','contactFunction');
const trackingWatchButton = getTrackingConfig('ad', 'buttons','watchFunction');

const ringSwapModalState = ref(false);

const onRestrictedFeatureClicked = (type: string) => {
  emit('update:more-actions-type', type);
};

const lastOnline = computed(() => {
  const date = props.user.lastActivity.date;
  if (timestampOlderThanOneMonth(date)) {
    return t('last-online-more-than-one-month-ago');
  }
  return t('last-online-on', [new Date(date).toLocaleDateString('de-DE')]);
});
</script>

<template>
  <section class="housing-info-card">
    <header class="housing-info-card-header">
      <div class="housing-info-card-header-user">
        <tw-user-avatar
          class="housing-info-card-header-user-avatar"
          size="2.5rem"
          letter-size="1.2rem"
          :is-clickable="false"
          :image-url="user.image.urlPaths['50x50']"
          :last-name="user.displayName.split(' ')[1]"
          :first-name="user.displayName.split(' ')[0]"
        />
        <div class="housing-info-card-header-user-info">
          <h3 class="housing-info-card-header-user-name">
            {{ user.displayName }}
          </h3>
          <p class="housing-info-card-header-user-last-online">
            {{ lastOnline }}
          </p>
        </div>
      </div>
      <img class="housing-info-card-header-image" :alt="housing.title" :src="housing.previewImage.urlPaths.small" loading="lazy" />
      <div class="housing-info-card-header-info">
        <h3 class="housing-info-card-header-heading">
          {{ housing.title }}
        </h3>
        <tw-housing-summary :housing="housing" :concise="true" />
        <address class="housing-info-card-header-address">
          {{ addressFromHousing(housing) }}
        </address>
      </div>
    </header>
    <div class="housing-info-card-content">
      <template  v-if="isLoggedIn">
      <tw-ring-swap-contact-modal
        v-if="offer && offer?.match && offer?.match?.chainLength  > 2"
        :show-trigger="true"
        :trigger-disabled="isEditing || isUsersOwnHousing"
        :housing-id="housing.id"
        :user-id="user.id"
        :state="ringSwapModalState"
        @close="ringSwapModalState = false"
        @open="ringSwapModalState = true"
      />
        <TwHousingBtnContact v-else :housing="housing" :disabled="isEditing || isUsersOwnHousing" />
      </template>
      <TwUiAction
        v-else
        class="tw-tracking"
        :is="'button'"
        type="button"
        variant="button-primary"
        @click="onRestrictedFeatureClicked('contact')"
        :data-page="trackingButton.dataPage"
        :data-tracking-id="trackingButton.dataTrackingId"
      >
        {{ $t('to-contact') }}
      </TwUiAction>
      <TwButtonWatchList
        v-if="!isLoggedIn"
        class="watch-list-button tw-tracking"
        :state="'removed'"
        :variant="'complete'"
        @click="onRestrictedFeatureClicked('toggle_like')"
        :data-page="trackingWatchButton.dataPage"
        :data-tracking-id="trackingWatchButton.dataTrackingId"
        data-button-color="secondary"
        data-button-icon="heart"
      />

      <TwButtonWatchList
        v-else-if="!offer?.actions.watched"
        class="watch-list-button"
        :state="'removed'"
        :variant="'complete'"
        :loading="watchListUpdateInProgress"
        :disabled="isEditing || isUsersOwnHousing || watchListUpdateInProgress"
        @click="emit('update:watch-list', true)"
      />

      <TwButtonWatchList
        v-else
        class="watch-list-button"
        :state="'added'"
        :variant="'complete'"
        :loading="watchListUpdateInProgress"
        :disabled="isEditing || isUsersOwnHousing || watchListUpdateInProgress"
        @click="emit('update:watch-list', false)"
      />
    </div>

    <div class="housing-info-card-bottom-bar">
      <tw-housing-btn-share :housing="housing" tracking-page="ad" />
      <TwHousingBtnReport v-if="isLoggedIn" :housing="housing" :disabled="isEditing || isUsersOwnHousing" />
      <button v-else type="button" class="housing-info-card-bottom-bar-btn-report-restricted" @click="onRestrictedFeatureClicked('report')">
        <Icon name="bi:exclamation-triangle" />
        {{ $t('to-report') }}
      </button>
    </div>
  </section>
</template>

<style scoped lang="scss">
@import '~/assets/styles/tw-variables.scss';
@import '~/assets/styles/tw-mixins.scss';
@import '~/assets/styles/tw-form.scss';

.housing-info-card {
  gap: 1rem;
  display: flex;
  border-radius: 0.3rem;
  flex-direction: column;
  border: 1px solid $lightGray;
}

.housing-info-card-header {
  position: relative;
}

.housing-info-card-header-user {
  gap: 0.5rem;
  display: flex;
  align-items: center;
  padding: 0.75rem 0.5rem;
}

.housing-info-card-header-user-avatar {
  width: 2.5rem;
  height: 2.5rem;
  object-fit: cover;
  border-radius: 100%;
  object-position: center;
}

.housing-info-card-header-user-info > * {
  margin: 0;
}

.housing-info-card-header-user-name {
  font-size: 1rem;
  line-height: normal;
}

.housing-info-card-header-user-last-online {
  color: $neutral-70;
}

.housing-info-card-header-image {
  width: 100%;
  display: block;
  object-fit: cover;
  object-position: center;
}

.housing-info-card-header-info {
  bottom: 0;
  width: 100%;
  gap: 0.25rem;
  color: $white;
  display: flex;
  padding: 1rem;
  position: absolute;
  flex-direction: column;
  background: rgba($black, 0.6);
}

.housing-info-card-header-heading {
  margin: 0;
  @include tw-h5;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.housing-info-card-header-address {
  font-style: normal;
}

.housing-info-card-content {
  gap: 1rem;
  display: flex;
  padding: 0 1rem;
  flex-direction: column;
}

.housing-info-card-content :deep(button) {
  font-size: 1rem !important;
}

.housing-info-card-bottom-bar {
  width: 100%;
  display: flex;
  height: 3.5rem;
  background-color: $white;
  justify-content: space-around;
  border-top: 1px solid $lightGray;
  border-radius: 0 0 0.3rem 0.3rem;

  & > * {
    width: 50%;

    &:hover {
      color: rgba($black, 0.5);
    }
  }
}

.housing-info-card-bottom-bar-btn-report-restricted {
  @include tw-button-link;
  gap: 0.5rem;
}

.housing-info-card-bottom-bar-btn-like-restricted {
  @include tw-button-outlined;
  padding: 0.5rem 1rem;
}

.contact-modal {
 :deep(.tw-modal--title){
    text-align: center  ;
  }
  .i-bi\:chevron-left {
    cursor: pointer;
  }
}

</style>

<i18n lang="json">
{
  "de": {
    "contact": "Kontakt",
    "ring-swap-chats": "Ringtausch Chats",
    "swap-details": "Tauschdetails",
    "how-it-works": "So funktioniert es",
    "room": "Zimmer | Zimmer",
    "offer": "Angebot"
  },
  "en": {
    "contact": "Contact",
    "ring-swap-chats": "Ring swap chats",
    "swap-details": "Swap details",
    "how-it-works": "How it works",
    "room": "Room | Rooms",
    "offer": "Offer"
  }
}
</i18n>
